import Grid from '@mui/material/Grid';
import { Theme } from '@mui/material/styles';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import { bootstrap } from '../../../store/dialer';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import IncomingCallControls from '../IncomingCall/IncomingCallControls';
import ActiveCallContact from './ActiveCallContact';
import ActiveCallControls from './ActiveCallControls';

export default function ActiveCall() {
    const { classes } = useStyles();
    const dispatch = useAppDispatch();

    const isImpersonated = useAppSelector(state => state.user.isImpersonated);
    const isDialerBootstrapped = useAppSelector(state => state.dialer.isBootstrapped);
    const twilioCall = useAppSelector(state => state.dialer.twilioCall);
    const callStartTime = useAppSelector(state => state.dialer.callStart);

    React.useEffect(() => {
        if (!isDialerBootstrapped && !isImpersonated) {
            //only bootstrap for non-impersonated users
            dispatch(bootstrap());
        }
    }, [isDialerBootstrapped, isImpersonated, dispatch]);

    /*
        Expected Behavior: should only be mounted when there is an active call, leave unmounted
        if there is an incoming call that has not been accepted. Controls to include in-call
        options, no 'accept call' action.

        TIP: component must be "self mounting" to keep call logic out of PageLayout, and must
        be absolutely positioned so as to be a child of PageLayout while appearing to be a
        child of PageHeader. Child of PageLayout so each page navigation does not cause
        timer to reset to 0 before being calculated again.
     */
    return (
        <React.Fragment>
            {twilioCall && (
                <div className={classes.root}>
                    <div className={classes.alert}>
                        <Grid
                            container
                            direction="row"
                            wrap="nowrap"
                            alignItems="center"
                            className={classes.flexWrapper}
                        >
                            <Grid item xs={true}>
                                <ActiveCallContact
                                    twilioCall={twilioCall}
                                    callStartTime={callStartTime}
                                />
                            </Grid>
                            <Grid item xs="auto">
                                {twilioCall.status === 'pending' && <IncomingCallControls />}
                                {twilioCall.status !== 'pending' && <ActiveCallControls />}
                            </Grid>
                        </Grid>
                    </div>
                </div>
            )}
        </React.Fragment>
    );
}

const useStyles = makeStyles()((theme: Theme) => ({
    root: {
        position: 'absolute',
        top: 0,
        zIndex: theme.zIndex.appBar,
        /* 
            TIP: heights follow $app-header-height CSS variable for screen sizes xs, lg, xl,
            widths follow $contact-aside-width CSS variable for screen sizes md, lg, xl
         */
        [theme.breakpoints.down('md')]: {
            height: '3rem',
            width: '100%',
            color: '0',
        },
        [theme.breakpoints.up('md')]: {
            right: '4rem',
            height: '3rem',
            width: 'calc(360px - 3rem)',
            color: '1',
        },
        [theme.breakpoints.up('lg')]: {
            width: 'calc(400px - 3rem)',
            height: '3rem',
            color: '2',
        },
        [theme.breakpoints.up('xl')]: {
            right: '4rem',
            height: '3.6rem',
            width: 'calc(480px - 4rem)',
            color: '3',
        },
    },
    alert: {
        width: '100%',
        height: '100%',
        padding: theme.spacing(0, 2),
        backgroundColor: 'rgb(237, 247, 237)',
        borderRadius: 0,
    },
    flexWrapper: {
        height: '100%',
    },
}));
